import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { AuthService, UserProfile, AuthResponse } from '../shared/api/authService';

// 认证状态类型
interface AuthState {
  isAuthenticated: boolean;
  user: UserProfile | null;
  loading: boolean;
  error: string | null;
}

// 认证Action类型
type AuthAction =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: UserProfile }
  | { type: 'LOGIN_FAILURE'; payload: string }
  | { type: 'LOGOUT' }
  | { type: 'LOAD_USER_START' }
  | { type: 'LOAD_USER_SUCCESS'; payload: UserProfile }
  | { type: 'LOAD_USER_FAILURE'; payload: string }
  | { type: 'UPDATE_USER'; payload: UserProfile }
  | { type: 'CLEAR_ERROR' };

// 认证Context类型
interface AuthContextType {
  state: AuthState;
  login: (email: string, password: string) => Promise<void>;
  loginWithUser: (user: UserProfile) => void;
  register: (email: string, password: string, displayName: string, language?: string) => Promise<void>;
  logout: () => void;
  updateProfile: (data: Partial<UserProfile>) => Promise<void>;
  clearError: () => void;
}

// 初始状态
const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  loading: false,
  error: null,
};

// Reducer
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'LOGIN_START':
    case 'LOAD_USER_START':
      return {
        ...state,
        loading: true,
        error: null,
      };
      
    case 'LOGIN_SUCCESS':
    case 'LOAD_USER_SUCCESS':
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload,
        loading: false,
        error: null,
      };
      
    case 'LOGIN_FAILURE':
    case 'LOAD_USER_FAILURE':
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        loading: false,
        error: action.payload,
      };
      
    case 'LOGOUT':
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        loading: false,
        error: null,
      };
      
    case 'UPDATE_USER':
      return {
        ...state,
        user: action.payload,
      };
      
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
      
    default:
      return state;
  }
};

// 创建Context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider组件
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // 页面加载时检查用户认证状态
  useEffect(() => {
    const loadUser = async () => {
      if (AuthService.isTokenValid()) {
        dispatch({ type: 'LOAD_USER_START' });
        try {
          const user = await AuthService.getCurrentUser();
          dispatch({ type: 'LOAD_USER_SUCCESS', payload: user });
        } catch (error) {
          // 如果令牌无效，清理本地存储
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
        }
      }
    };

    loadUser();
  }, []);

  // 登录方法
  const login = async (email: string, password: string): Promise<void> => {
    dispatch({ type: 'LOGIN_START' });
    try {
      const authResponse: AuthResponse = await AuthService.login({ email, password });
      AuthService.saveTokens(authResponse);
      const user = await AuthService.getCurrentUser();
      dispatch({ type: 'LOGIN_SUCCESS', payload: user });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '登录失败';
      dispatch({ type: 'LOGIN_FAILURE', payload: errorMessage });
      throw error;
    }
  };

  // 注册方法
  const register = async (
    email: string, 
    password: string, 
    displayName: string, 
    language: string = 'ja'
  ): Promise<void> => {
    dispatch({ type: 'LOGIN_START' });
    try {
      const authResponse: AuthResponse = await AuthService.register({
        email,
        password,
        display_name: displayName,
        preferred_language: language as 'zh' | 'ja' | 'en',
      });
      AuthService.saveTokens(authResponse);
      
      const user = await AuthService.getCurrentUser();
      dispatch({ type: 'LOGIN_SUCCESS', payload: user });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '注册失败';
      dispatch({ type: 'LOGIN_FAILURE', payload: errorMessage });
      throw error;
    }
  };

  // 登出方法
  const logout = (): void => {
    AuthService.logout();
    dispatch({ type: 'LOGOUT' });
  };

  // 更新用户资料
  const updateProfile = async (data: Partial<UserProfile>): Promise<void> => {
    try {
      const updatedUser = await AuthService.updateUserProfile(data);
      dispatch({ type: 'UPDATE_USER', payload: updatedUser });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '更新资料失败';
      dispatch({ type: 'LOGIN_FAILURE', payload: errorMessage });
      throw error;
    }
  };

  // 使用已有用户信息登录（用于Google登录等）
  const loginWithUser = (user: UserProfile): void => {
    dispatch({ type: 'LOGIN_SUCCESS', payload: user });
  };

  // 清除错误
  const clearError = (): void => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const value: AuthContextType = {
    state,
    login,
    loginWithUser,
    register,
    logout,
    updateProfile,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook for using the auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 